import { useEffect } from "react";
import { motion } from "framer-motion";
import { Utensils, ChefHat, Coffee, Truck, ShoppingBag, Factory, Warehouse, ConciergeBell, Package, MessageCircle } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../ui/accordion";
import { useQuote } from "./Header";
import { SITE } from "../../lib/config";
import { track } from "../../lib/analytics";

const ALL_COLORS = ["Black", "White", "Transparent"];
const TRANSPARENT_ONLY = ["Transparent"];

const COLOR_STYLES = {
  Black: "bg-[#0A1C0E] text-white border-[#0A1C0E]",
  White: "bg-white text-[#0A1C0E] border-[#E2E8E4]",
  Transparent: "bg-white/60 text-[#0A1C0E] border-dashed border-[#94A69A]",
};

const APPLICATIONS = [
  { icon: Utensils, t: "Restaurants" },
  { icon: ChefHat, t: "Cloud Kitchens" },
  { icon: Package, t: "Biryani Shops" },
  { icon: ConciergeBell, t: "Caterers" },
  { icon: ShoppingBag, t: "Takeaway Businesses" },
  { icon: Truck, t: "Food Delivery" },
  { icon: Coffee, t: "Canteens & Cafeterias" },
  { icon: Factory, t: "Food Manufacturers" },
  { icon: Warehouse, t: "Wholesale Distributors" },
];

const SIZES = [
  {
    label: "25 ML or 50 ML Containers",
    img: "/pp-sizes/25-50ml.jpg",
    tag: "Portion cups",
    desc: "Ideal for chutneys, sauces, dips, pickle, ghee and small condiments served with takeaway meals.",
    uses: "Sauces • Chutneys • Dips",
    colors: TRANSPARENT_ONLY,
  },
  {
    label: "100 ML or 120 ML Containers",
    img: "/pp-sizes/100-120ml.jpg",
    tag: "Sauce cups",
    desc: "Perfect for gravies, raita, sambar, kurma, ketchup, mayo and other side portions in food delivery.",
    uses: "Raita • Gravies • Sides",
    colors: ALL_COLORS,
  },
  {
    label: "250 ML Containers",
    img: "/pp-sizes/250ml.jpg",
    tag: "Curry cups",
    desc: "Best for individual curry portions, dals, soups, desserts and short-eat snacks for cloud kitchens and QSRs.",
    uses: "Curries • Dals • Desserts",
    colors: ALL_COLORS,
  },
  {
    label: "500 ML Containers",
    img: "/pp-sizes/500ml.jpg",
    tag: "Meal bowls",
    desc: "Single-serve meal containers for rice, noodles, salads, biryani and combo bowls in restaurants and takeaway.",
    uses: "Rice • Noodles • Salads",
    colors: ALL_COLORS,
  },
  {
    label: "600 ML or 750 ML Containers",
    img: "/pp-sizes/600-750ml.jpg",
    tag: "Family / party packs",
    desc: "Larger portion containers for biryani, family meals, party packs and catering orders across food-service businesses.",
    uses: "Biryani • Family meals • Catering",
    colors: ALL_COLORS,
  },
];

const TYPES = [
  "Round PP containers",
  "Rectangle PP containers",
  "PP containers with lids",
  "Disposable takeaway containers",
  "Meal / food containers",
  "Biryani containers",
];

const FAQS = [
  { q: "What are PP food containers used for?",
    a: "PP food containers are used by restaurants, cloud kitchens, caterers and takeaway businesses to pack meals, rice, biryani, curries, snacks and other food items for takeaway and delivery." },
  { q: "Do you supply disposable PP containers in wholesale quantities?",
    a: "Yes. Chennai Packs supplies disposable PP food containers in wholesale/bulk quantities to food-service businesses. Share your required size and quantity for pricing." },
  { q: "What sizes of PP food containers are available?",
    a: "Our PP food container range is available in multiple sizes suitable for different meal formats. Enquire with your required capacity and shape for exact availability." },
  { q: "Do PP containers come with lids?",
    a: "Yes, lids are available for the PP containers in our range. Please confirm size and lid type when requesting a quote." },
  { q: "Are PP containers suitable for restaurant takeaway?",
    a: "Yes. Our PP food containers are designed for takeaway and food delivery use across restaurants, QSRs and cloud kitchens." },
  { q: "Do you supply PP containers to cloud kitchens and caterers?",
    a: "Yes. Cloud kitchens and caterers are among our regular customers for PP food containers, meal trays and related takeaway packaging." },
  { q: "Do you supply PP food containers in Chennai?",
    a: "Yes. Chennai Packs is a Chennai-based supplier of disposable PP food containers, serving customers across Chennai, Tamil Nadu and pan-India." },
  { q: "How can I get a wholesale quote?",
    a: "Click ‘Get a Quote’ or ‘Enquire on WhatsApp’ on this page, share your required container size and quantity, and our team will respond with wholesale pricing." },
];

export default function PPContainersContent() {
  const openQuote = useQuote();
  const waHref = `https://wa.me/${SITE.whatsapp}?text=${encodeURIComponent("Hi Chennai Packs, I'd like a quote for Disposable PP Food Containers.")}`;

  // FAQ JSON-LD structured data (kept minimal, factual only)
  useEffect(() => {
    const s = document.createElement("script");
    s.type = "application/ld+json";
    s.setAttribute("data-pp-faq", "1");
    s.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: FAQS.map((f) => ({
        "@type": "Question", name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    });
    document.head.appendChild(s);
    return () => {
      document.head.querySelectorAll('script[data-pp-faq="1"]').forEach((n) => n.remove());
    };
  }, []);

  return (
    <>
      {/* Intro */}
      <section className="py-16" data-testid="pp-intro">
        <div className="max-w-4xl mx-auto px-5 md:px-10">
          <h2 className="font-head text-3xl md:text-4xl font-medium tracking-tight text-[#0A1C0E]">
            Disposable PP Food Containers for Restaurants & Food Businesses
          </h2>
          <p className="text-lg text-[#3A4D3E] mt-4 leading-relaxed">
            Chennai Packs supplies disposable PP food containers for restaurants, cloud kitchens,
            caterers, takeaway businesses and food-service operations. Our PP container range is
            available in multiple sizes and formats for packing meals, rice, biryani, curries,
            snacks and other takeaway foods.
          </p>
        </div>
      </section>

      {/* Sizes */}
      <section className="py-16 bg-[#F8FAF8]" data-testid="pp-sizes">
        <div className="max-w-6xl mx-auto px-5 md:px-10">
          <h2 className="font-head text-3xl md:text-4xl font-medium tracking-tight text-[#0A1C0E]">
            PP Food Container Sizes
          </h2>
          <p className="text-[#3A4D3E] mt-3 max-w-2xl">
            Our disposable PP food container range is stocked in multiple sizes to suit every
            portion — from small sauce cups to family meal packs. All sizes are available with
            matching lids on request.
          </p>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {SIZES.map((s) => (
              <article
                key={s.label}
                className="bg-white border border-[#E2E8E4] rounded-2xl overflow-hidden hover:-translate-y-1 hover:shadow-xl transition-all duration-500"
                data-testid={`pp-size-${s.label.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`}
              >
                <div className="aspect-[4/3] bg-[#F8FAF8] overflow-hidden">
                  <img
                    src={s.img}
                    alt={`${s.label} — disposable PP food container from Chennai Packs`}
                    loading="lazy"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-6">
                  <span className="text-[11px] font-bold uppercase tracking-widest text-[#1D6B2A]">
                    {s.tag}
                  </span>
                  <h3 className="font-head text-xl font-semibold text-[#0A1C0E] mt-1">
                    {s.label}
                  </h3>
                  <p className="text-sm text-[#3A4D3E] mt-2 leading-relaxed">{s.desc}</p>
                  <p className="text-xs text-[#0A1C0E] mt-3 font-medium">{s.uses}</p>
                  <div className="mt-4">
                    <p className="text-[11px] font-bold uppercase tracking-widest text-[#3A4D3E] mb-2">
                      {s.colors.length === 1 ? "Available in" : "Tap a colour to WhatsApp us"}
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      {s.colors.map((color) => {
                        const sizeShort = s.label.split(" Containers")[0];
                        const waMsg = `Hi Chennai Packs, I'd like a quote for Disposable PP Food Containers — ${sizeShort} (${color}). Please share pricing and MOQ. Thank you.`;
                        const waUrl = `https://wa.me/${SITE.whatsapp}?text=${encodeURIComponent(waMsg)}`;
                        return (
                          <a
                            key={color}
                            href={waUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={() => track("PP Colour WhatsApp", { size: sizeShort, color })}
                            data-testid={`pp-color-${s.label.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}-${color.toLowerCase()}`}
                            title={`WhatsApp us for ${sizeShort} in ${color}`}
                            className={`inline-flex items-center gap-1 border ${COLOR_STYLES[color]} hover:opacity-80 rounded-full px-3 py-1 text-xs font-semibold transition-opacity`}
                          >
                            <span className={`w-2.5 h-2.5 rounded-full border ${color === 'Black' ? 'bg-black border-black' : color === 'White' ? 'bg-white border-[#94A69A]' : 'bg-transparent border-[#94A69A]'}`} />
                            {color}
                            <MessageCircle size={12} className="opacity-80" />
                          </a>
                        );
                      })}
                    </div>
                  </div>
                  <div className="mt-5 flex flex-col gap-2">
                    {(() => {
                      const sizeShort = s.label.split(" Containers")[0];
                      const waMsg = `Hi Chennai Packs, I'd like a quote for Disposable PP Food Containers — ${sizeShort}. Please share pricing and MOQ. Thank you.`;
                      const waUrl = `https://wa.me/${SITE.whatsapp}?text=${encodeURIComponent(waMsg)}`;
                      return (
                        <a
                          href={waUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={() => track("PP Size WhatsApp", { size: sizeShort })}
                          data-testid={`pp-size-wa-${s.label.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`}
                          className="inline-flex items-center justify-center gap-2 border border-[#1D6B2A] text-[#1D6B2A] rounded-full px-4 py-2 text-xs font-semibold hover:bg-[#F8FAF8] transition-colors"
                        >
                          <MessageCircle size={14} /> WhatsApp for {sizeShort}
                        </a>
                      );
                    })()}
                    <button
                      onClick={() => openQuote({ name: `Disposable PP Food Containers — ${s.label}`, sku: "CP-011" })}
                      data-testid={`pp-size-quote-${s.label.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`}
                      className="bg-[#1D6B2A] text-white rounded-full px-5 py-2 text-xs font-semibold hover:bg-[#154d1e] transition-colors"
                    >
                      Get a Quote for {s.label.split(" Containers")[0]}
                    </button>
                  </div>
                </div>
              </article>
            ))}
          </div>
          <p className="text-sm text-[#3A4D3E] mt-8 text-center">
            Share your required capacity and quantity — our team will confirm exact stock, lid
            options and wholesale pricing.
          </p>
        </div>
      </section>

      {/* Applications */}
      <section className="py-16" data-testid="pp-applications">
        <div className="max-w-6xl mx-auto px-5 md:px-10">
          <h2 className="font-head text-3xl md:text-4xl font-medium tracking-tight text-[#0A1C0E]">
            Ideal for Food Takeaway & Delivery
          </h2>
          <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {APPLICATIONS.map((a) => (
              <div key={a.t} className="bg-white border border-[#E2E8E4] rounded-2xl p-5 flex items-center gap-4">
                <span className="grid place-items-center w-11 h-11 rounded-xl bg-[#F8FAF8] text-[#1D6B2A] shrink-0">
                  <a.icon size={22} />
                </span>
                <span className="font-head font-semibold text-[#0A1C0E]">{a.t}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Types */}
      <section className="py-16 bg-[#F8FAF8]" data-testid="pp-types">
        <div className="max-w-5xl mx-auto px-5 md:px-10">
          <h2 className="font-head text-3xl md:text-4xl font-medium tracking-tight text-[#0A1C0E]">
            PP Food Packaging Options
          </h2>
          <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {TYPES.map((t) => (
              <div key={t} className="bg-white border border-[#E2E8E4] rounded-xl px-5 py-4 text-[#0A1C0E]">
                {t}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Wholesale */}
      <section className="py-16" data-testid="pp-wholesale">
        <div className="max-w-4xl mx-auto px-5 md:px-10">
          <h2 className="font-head text-3xl md:text-4xl font-medium tracking-tight text-[#0A1C0E]">
            Wholesale PP Food Containers in Chennai
          </h2>
          <p className="text-lg text-[#3A4D3E] mt-4 leading-relaxed">
            Chennai Packs supplies disposable PP food containers to restaurants, cloud kitchens,
            caterers, takeaway businesses and other food-service customers in Chennai. Enquire
            with your required size and quantity for availability and wholesale pricing.
          </p>
          <div className="mt-6 flex flex-wrap gap-4">
            <button
              onClick={() => openQuote({ name: "Disposable PP Food Containers", sku: "CP-011" })}
              data-testid="pp-wholesale-quote"
              className="bg-[#1D6B2A] text-white rounded-full px-8 py-4 font-semibold hover:bg-[#154d1e] transition-colors"
            >
              Get a Quote
            </button>
            <a
              href={waHref}
              target="_blank"
              rel="noopener noreferrer"
              className="border border-[#1D6B2A] text-[#1D6B2A] rounded-full px-8 py-4 font-semibold hover:bg-[#F8FAF8] transition-colors"
            >
              Enquire on WhatsApp
            </a>
          </div>
        </div>
      </section>

      {/* Custom Branding */}
      <section className="py-16 bg-[#F8FAF8]" data-testid="pp-branding">
        <div className="max-w-4xl mx-auto px-5 md:px-10">
          <h2 className="font-head text-3xl md:text-4xl font-medium tracking-tight text-[#0A1C0E]">
            Custom Branded Food Packaging
          </h2>
          <p className="text-lg text-[#3A4D3E] mt-4 leading-relaxed">
            Eligible packaging products can be considered for custom printing with your logo and
            artwork — a simple way to build brand recall on every delivery. Custom-branding
            availability depends on the specific product, size and minimum order quantity. Share
            your requirement and our team will confirm whether custom branding is possible for the
            containers you need.
          </p>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16" data-testid="pp-faq">
        <div className="max-w-3xl mx-auto px-5 md:px-10">
          <h2 className="font-head text-3xl md:text-4xl font-medium tracking-tight text-[#0A1C0E]">
            Frequently Asked Questions
          </h2>
          <Accordion type="single" collapsible className="space-y-3 mt-8">
            {FAQS.map((f, i) => (
              <AccordionItem key={i} value={`pp-${i}`} className="bg-white border border-[#E2E8E4] rounded-2xl px-6 data-[state=open]:shadow-md transition-shadow">
                <AccordionTrigger className="font-head text-base font-semibold text-[#0A1C0E] hover:no-underline text-left py-5">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-[#3A4D3E] leading-relaxed pb-5">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Internal links */}
      <section className="py-12 bg-[#F8FAF8]" data-testid="pp-related-links">
        <div className="max-w-5xl mx-auto px-5 md:px-10">
          <h3 className="font-head text-xl font-semibold text-[#0A1C0E]">Related packaging you may need</h3>
          <div className="mt-4 flex flex-wrap gap-3">
            {[
              { to: "/products/plastic-meal-tray-with-lid", label: "Meal Trays" },
              { to: "/products/aluminium-foil-containers", label: "Aluminium Foil Containers" },
              { to: "/products/juice-pet-bottles", label: "Juice Bottles" },
              { to: "/products?category=paper-packaging", label: "Paper Packaging" },
              { to: "/products/paper-meal-box-containers", label: "Paper Meal Boxes" },
            ].map((l) => (
              <a key={l.to} href={l.to} className="border border-[#E2E8E4] bg-white rounded-full px-5 py-2 text-sm font-medium text-[#1D6B2A] hover:bg-[#F0F4F0]">
                {l.label}
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16" data-testid="pp-final-cta">
        <div className="max-w-4xl mx-auto px-5 md:px-10 text-center">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="font-head text-3xl md:text-4xl font-medium tracking-tight text-[#0A1C0E]"
          >
            Looking for PP Food Containers in Chennai?
          </motion.h2>
          <p className="text-lg text-[#3A4D3E] mt-4 max-w-2xl mx-auto">
            Tell us your required container size and quantity. Enquire with Chennai Packs for
            availability and wholesale pricing.
          </p>
          <div className="mt-8 flex flex-wrap gap-4 justify-center">
            <button
              onClick={() => openQuote({ name: "Disposable PP Food Containers", sku: "CP-011" })}
              data-testid="pp-final-quote"
              className="bg-[#1D6B2A] text-white rounded-full px-8 py-4 font-semibold hover:bg-[#154d1e] transition-colors"
            >
              Get a Quote
            </button>
            <a
              href={waHref}
              target="_blank"
              rel="noopener noreferrer"
              className="border border-[#1D6B2A] text-[#1D6B2A] rounded-full px-8 py-4 font-semibold hover:bg-[#F8FAF8] transition-colors"
            >
              Enquire on WhatsApp
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
